import React, { useState } from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import orderBy from 'lodash/orderBy';
import * as Bixby from '$/core';
import BixButton from '$/components/global/bixButton';
import BixbyQuillWrapper from '$/components/quill';
import BixCheckbox from '$/components/global/bixCheckbox';
import InputBlock from '$/components/global/inputBlock';
import Modal from '$/components/global/modal';
import NewsItem from '$/components/newsItem';
import PeopleSearchBox from '$/components/global/peopleSearchBox';
import classnames from 'classnames';
import './index.scss';
import CompanyDocumentFileUpload from '$/components/company-documents-upload/companyDocumentFileUpload';
import { DocumentsAdminService, NewsAdminService } from '$/services';
import { FileValidationRequest } from '$/services/DocumentsAdminService';
import Loading from '$/components/loading/Loading';
import dayjs from 'dayjs';
import { DeclarationType } from '$/core/enums';

interface IProps {
  filters?: Bixby.Models.IIdentifiable[];
  initialValues: any;
  handleSubmit: (value: Bixby.Models.NewsValues) => void;
  validationSchema: Yup.ObjectSchema<any>;
  hiddenFields?: string[];
  readOnlyFields?: string[];
  transcriptionClient?: Bixby.Models.AdminCompanyNewsClientCompany;
  company?: Bixby.Models.AdminCompanyNews;
  extraConferSteps?: Bixby.Models.ExtraConferStep[];
  additionalDetails?: string;
  adminCompany?: Bixby.Models.AdminCompany;
  companyDocumentCategories?: Bixby.Models.CompanyDocumentCategory[];
  currentlySubmittingDocumentStatus: string | undefined;
  isSubmittingNews: boolean;
  isDocumentHostingEnabled: boolean;
  dispatchToastSuccess: (messagine: string) => Promise<void>;
  dispatchToastError: (messagine: string) => Promise<void>;
}

const DatePickerField = ({
  name,
  value,
  minDate,
  maxDate,
  onChange,
  showTimeSelect,
  timeIntervals,
  dateFormat,
  isRestrictBackdate,
  disabled,
}: {
  [key: string]: any;
}) => {
  const fieldProps: ReactDatePickerProps = {
    showTimeSelect: showTimeSelect,
    timeIntervals: timeIntervals || 15,
    minDate: minDate || null,
    maxDate: maxDate,
    dateFormat: dateFormat || 'MM/dd/yyyy',
    selected: (value && new Date(value)) || null,
    onChange: (val: any) => {
      onChange(name, val);
    },
    disabled,
  };

  if (!isRestrictBackdate) {
    fieldProps.onKeyDown = e => {
      e.preventDefault();
    };
  }

  return <DatePicker {...fieldProps} />;
};

const CreateNewsForm: React.FC<IProps> = props => {
  const {
    filters,
    initialValues,
    handleSubmit,
    validationSchema,
    transcriptionClient,
    company,
    extraConferSteps,
    hiddenFields,
    readOnlyFields,
    additionalDetails,
    adminCompany,
    companyDocumentCategories,
    currentlySubmittingDocumentStatus,
    isSubmittingNews,
    dispatchToastError,
    dispatchToastSuccess,
    isDocumentHostingEnabled,
  } = props;
  const [lenderCallDetailsVisible, setLenderCallDetailsVisible] = useState(false);
  const [hasTranscriptionTaskEnabled, setHasTranscriptionTaskEnabled] = useState(false);
  const [modalType, setModalType] = useState(Bixby.Enums.ModalTypes.None);
  const [confirmUpload, setConfirmUpload] = useState(false);
  const [documentValidationError, setDocumentValidationError] = useState(undefined as string | undefined);
  const [isValidatingDocuments, setIsValidatingDocuments] = useState(false);
  const [earningsReleaseDate, setEarningsReleaseDate] = useState(undefined as string | undefined);
  const [earningsReleaseSubject, setEarningsReleaseSubject] = useState(undefined as string | undefined);
  // const [noDocumentsCheckValue, setNoDocumentsCheckValue] = useState(false);

  const alertTypes = (filters || []).map(filter => {
    return (
      <option value={filter.id} key={filter.id}>
        {filter.name}
      </option>
    );
  });

  const defaultTimezonesOptions = (Bixby.Constants.DEFAULTTIMEZONES || []).map(tz => {
    return (
      <option value={tz.Id} key={tz.Id} className="default_option">
        {tz.DisplayName}
      </option>
    );
  });

  const timezoneOptions = (Bixby.Constants.TIMEZONES || []).map(tz => {
    return (
      <option value={tz.Id} key={tz.Id}>
        {tz.DisplayName}
      </option>
    );
  });

  const handleHasAppointmentCheck = (setFieldValue: any, currentValue: boolean) => {
    setFieldValue('hasAppointment', !currentValue);
  };

  const handleHasTranscriptionTaskCheck = (setFieldValue: any, currentValue: boolean) => {
    setFieldValue('hasTranscriptionTask', !currentValue);
  };

  const handleSetDroppedFiles = (setFieldValue: any, currentValue: Bixby.Models.IDocumentFile[]) => {
    setFieldValue('droppedFiles', currentValue);
  };

  const handlePreviewModalClick = () => {
    setModalType(Bixby.Enums.ModalTypes.PreviewModal);
  };

  const handleCloseModal = () => {
    setModalType(Bixby.Enums.ModalTypes.None);
  };

  const handleSubmitClick = () => {
    setConfirmUpload(true);
  };

  const handleSubjectTextChange = (setFieldValue: any, newsType_Id: string, description: string) => {
    const selectedNewsType = filters ? filters.find(_ => _.id === newsType_Id) : undefined;
    const newsText = selectedNewsType ? ` (${selectedNewsType.name}):` : '';
    const companyText = company ? company.name : '';
    const descriptionText = description ? ` ${description}` : '';
    const subjectText = `${companyText}${newsText}${descriptionText}`;
    setFieldValue('subject', subjectText);
  };

  const handleDaysUntilExpirationChange = (setFieldValue: any, values: Bixby.Models.NewsValues, days: number) => {
    setFieldValue('transcriptionDetails.daysUntilExpiration', days);
    setFieldValue(
      'transcriptionDetails.replayGoodUntil',
      dayjs(values.transcriptionDetails.replayGoodFrom).add(days, 'days').startOf('day').toDate()
    );
  };

  const handleReplayGoodUntilChange = (setFieldValue: any, values: Bixby.Models.NewsValues, date: Date) => {
    setFieldValue('transcriptionDetails.replayGoodUntil', date);
    setFieldValue(
      'transcriptionDetails.daysUntilExpiration',
      dayjs(date).diff(dayjs(values.transcriptionDetails.replayGoodFrom), 'days')
    );
  };

  const handleEndDateChange = (setFieldValue: any, values: Bixby.Models.NewsValues, endDate: Date | dayjs.Dayjs) => {
    const dayjsEndDate = dayjs(endDate);
    setFieldValue('appointment.endDate', dayjsEndDate.toDate());
    setFieldValue('transcriptionDetails.replayGoodFrom', dayjsEndDate.toDate());

    if (Bixby.Utils.isDefined(values.transcriptionDetails.daysUntilExpiration)) {
      setFieldValue(
        'transcriptionDetails.replayGoodUntil',
        dayjs(dayjsEndDate).add(values.transcriptionDetails.daysUntilExpiration, 'days').startOf('day').toDate()
      );
    }
  };

  const handleStartDateChange = (setFieldValue: any, values: Bixby.Models.NewsValues, startDate: Date) => {
    if (startDate) {
      const endDate = dayjs(startDate).add(1, 'hours').toDate();
      handleEndDateChange(setFieldValue, values, endDate);
    }
  };

  const alertTypeById = (id: string) => {
    if (alertTypes) {
      const found = alertTypes.find(alert => alert.key === id);
      if (found) {
        return found.props.children;
      }
    }
    return '';
  };

  const hiddenFieldsContains = (field: string) => {
    return (hiddenFields || []).includes(field);
  };

  const readOnlyFieldsContains = (field: string) => {
    return (readOnlyFields || []).includes(field);
  };

  const handleValidateDocuments = async (setFieldValue: any, droppedFiles: Bixby.Models.IDocumentFile[]) => {
    try {
      if (!adminCompany) return;

      setIsValidatingDocuments(true);
      setDocumentValidationError(undefined);
      const documents = droppedFiles.map((file: Bixby.Models.IDocumentFile) => {
        const model: FileValidationRequest = {
          id: file.id,
          fileName: file.file.name,
          title: file.title,
          description: file.description || '',
          companyDocumentCategory_Ids: file.companyDocumentCompanyDocumentCategories
            ? file.companyDocumentCompanyDocumentCategories.map(_ => _.companyDocumentCategory_Id)
            : [],
        };
        return model;
      });

      const results = await DocumentsAdminService.validateDocuments(adminCompany.id, documents);
      const mapped = results.map(result => {
        const found = droppedFiles.find(_ => _.id === result.inputFileId);
        if (!found) return;

        found.hasServerValidationError = !result.isValid;
        found.error = result.error;
        return found;
      });
      handleSetDroppedFiles(setFieldValue, [...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])]);
      dispatchToastSuccess('Validation check complete.');
    } catch (err: any) {
      dispatchToastError('Validation check failed');
      if (err && err.response && err.response.data && err.response.data.error) {
        setDocumentValidationError(err.response.data.error);
      } else {
        setDocumentValidationError('Error validating documents');
      }
    } finally {
      setIsValidatingDocuments(false);
    }
  };

  return (
    <section className="create_news_form">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: Bixby.Models.NewsValues) => {
          setModalType(Bixby.Enums.ModalTypes.ConfirmModal);
          if (confirmUpload) {
            handleSubmit(values);
            setModalType(Bixby.Enums.ModalTypes.None);
            setConfirmUpload(false);
          }
        }}
        render={({ handleSubmit, values, setFieldValue, handleBlur, setFieldTouched }) => (
          <div className="search_blocks">
            <form onSubmit={handleSubmit} className="profile-input-container">
              <div className="profile-input-column">
                <Field
                  name="company_Id"
                  render={() => {
                    return (
                      <InputBlock title="Bixby ID">
                        <input name="company_Id" value={company?.bixbyId} placeholder="Enter Bixby ID" disabled />
                      </InputBlock>
                    );
                  }}
                />
                <ErrorMessage name="company_Id">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                {!hiddenFieldsContains('description') && (
                  <>
                    <InputBlock title="Description">
                      <Field
                        type="text"
                        name="description"
                        render={({ form, field }: { [key: string]: any }) => {
                          return (
                            <input
                              disabled={readOnlyFieldsContains('description')}
                              name="description"
                              placeholder="Enter Description"
                              onBlur={handleBlur}
                              value={field.value}
                              onChange={e => {
                                form.setFieldValue('description', e.target.value);
                                handleSubjectTextChange(setFieldValue, values.newsType_Id, e.target.value);
                              }}
                            />
                          );
                        }}
                      />
                    </InputBlock>
                    <ErrorMessage name="description">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </>
                )}
                <InputBlock title="Subject">
                  <Field
                    type="text"
                    name="subject"
                    render={({ field }: { [key: string]: any }) => {
                      return (
                        <input
                          placeholder="Enter Subject"
                          name="subject"
                          value={field.value}
                          disabled
                          onChange={field.onChange(field.name)}
                        />
                      );
                    }}
                  />
                </InputBlock>
                <ErrorMessage name="subject">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
              </div>
              <div className="profile-input-column">
                {!hiddenFieldsContains('newsType_Id') && (
                  <>
                    <InputBlock title="Alert Type">
                      <Field
                        readOnly={readOnlyFieldsContains('description')}
                        name="newsType_Id"
                        render={({ form }: { [key: string]: any }) => {
                          return (
                            <select
                              disabled={readOnlyFieldsContains('newsType_Id')}
                              value={values.newsType_Id}
                              required
                              onBlur={() => setFieldTouched('newsType_Id')}
                              onChange={e => {
                                form.setFieldValue('newsType_Id', e.currentTarget.value);
                                handleSubjectTextChange(setFieldValue, e.currentTarget.value, values.description);
                                const alertType = filters
                                  ? filters.find(_ => _.id === e.currentTarget.value)
                                  : undefined;
                                const shouldShowLenderCallDetails =
                                  alertType && alertType.name === Bixby.Enums.NewsEarningsFilter.LenderCall
                                    ? true
                                    : false;
                                setFieldValue('hasAppointment', shouldShowLenderCallDetails);

                                const hasTranscriptionTask = shouldShowLenderCallDetails && !!transcriptionClient;
                                setFieldValue('hasTranscriptionTask', hasTranscriptionTask);
                                setHasTranscriptionTaskEnabled(hasTranscriptionTask);

                                setLenderCallDetailsVisible(shouldShowLenderCallDetails);
                                const isEarningsRelease =
                                  alertType && alertType.name === Bixby.Enums.NewsEarningsFilter.EarningsRelease;

                                // resetting the earnings release date
                                setEarningsReleaseDate(undefined);
                                // Check if the alert type is EarningsRelease type and company has values
                                if (company && isEarningsRelease) {
                                  // get news based on company id and news type id
                                  NewsAdminService.getNewsByCompanyIdAndNewsTypeId(company.id, alertType.id).then(
                                    newsList => {
                                      // assign the latest news date if count > 0
                                      if (newsList.length > 0) {
                                        const latestNews = orderBy(newsList, ['date'], ['desc'])[0];
                                        const newsDate = Bixby.Utils.utcDateAsLocal(latestNews.date);
                                        const formattedNewsDate = Bixby.Utils.formatDate(newsDate);
                                        setEarningsReleaseDate(formattedNewsDate);
                                        setEarningsReleaseSubject(latestNews.subject);
                                      }
                                    }
                                  );
                                }
                              }}>
                              <option hidden disabled key="-1" value="">
                                Select alert type
                              </option>
                              {alertTypes}
                            </select>
                          );
                        }}
                      />
                    </InputBlock>
                    <ErrorMessage name="newsType_Id">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </>
                )}
                {earningsReleaseDate && (
                  <span>
                    {`Latest Earnings Release ${earningsReleaseDate}`} <strong>({earningsReleaseSubject})</strong>
                  </span>
                )}
                {company &&
                  Bixby.Utils.isDefined(company.restrictNewsBackdate) &&
                  company.restrictNewsBackdate === false &&
                  !hiddenFieldsContains('backDateNews') && (
                    <div>
                      <Field
                        name="backDateNews"
                        render={({ field }: { [key: string]: any }) => (
                          <BixCheckbox
                            id="backDateNews"
                            checked={field.value}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const fieldName: keyof Bixby.Models.NewsValues = 'backDateNews';
                              setFieldValue(fieldName, e.target.checked);
                            }}>
                            Backdate
                          </BixCheckbox>
                        )}
                      />
                      {values.backDateNews && (
                        <InputBlock title="">
                          <DatePickerField
                            name="date"
                            value={values.date}
                            onChange={setFieldValue}
                            showTimeSelect={false}
                            maxDate={new Date()}
                            isRestrictBackdate={true}
                          />
                        </InputBlock>
                      )}
                      {values.backDateNews && (
                        <ErrorMessage name="date">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                      )}
                    </div>
                  )}
                <Field
                  name="sendEmail"
                  render={({ field }: { [key: string]: any }) => (
                    <BixCheckbox id="sendEmail" checked={field.value} onChange={field.onChange(field.name)}>
                      Send Company Update Email
                    </BixCheckbox>
                  )}
                />

                {lenderCallDetailsVisible && (
                  <>
                    {!hiddenFieldsContains('hasAppointment') && (
                      <div>
                        <BixCheckbox
                          id="hasAppointment"
                          checked={values.hasAppointment}
                          onChange={() => handleHasAppointmentCheck(setFieldValue, !!values.hasAppointment)}>
                          Has Appointment
                        </BixCheckbox>
                      </div>
                    )}
                    {!hiddenFieldsContains('hasTranscriptionTask') && (
                      <div>
                        <BixCheckbox
                          id="hasTranscriptionTask"
                          checked={values.hasTranscriptionTask}
                          disabled={!hasTranscriptionTaskEnabled}
                          onChange={() =>
                            handleHasTranscriptionTaskCheck(setFieldValue, !!values.hasTranscriptionTask)
                          }>
                          Assign Transcription Task
                        </BixCheckbox>
                        {!transcriptionClient && (
                          <div className="error error-message">
                            No clients who have access to this company have access to transcripts.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {values.hasAppointment && (
                <React.Fragment>
                  {!hiddenFieldsContains('appointment.timezone') && (
                    <div className="email_template">
                      <InputBlock title="Timezone">
                        <Field
                          name="timezone"
                          render={({ form }: { [key: string]: any }) => {
                            return (
                              <select
                                defaultValue=""
                                required
                                onBlur={() => setFieldTouched('appointment.timezone')}
                                onChange={e => {
                                  form.setFieldValue('appointment.timezone', e.currentTarget.value);
                                }}
                                value={values.appointment.timezone}
                                disabled={readOnlyFieldsContains('appointment.timezone')}>
                                <option hidden disabled value="" key="-1">
                                  Select timezone
                                </option>
                                {defaultTimezonesOptions}
                                <option disabled className="divider"></option>
                                {timezoneOptions}
                              </select>
                            );
                          }}
                        />
                      </InputBlock>
                      <ErrorMessage name="timezone">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                    </div>
                  )}
                  <div className="profile-input-column">
                    {!readOnlyFieldsContains('appointment.startDate') && (
                      <>
                        <InputBlock title="Start Date & Time">
                          <DatePickerField
                            name="appointment.startDate"
                            value={values.appointment.startDate}
                            onChange={(_field, value) => {
                              setFieldValue('appointment.startDate', value);
                              handleStartDateChange(setFieldValue, values, value);
                            }}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            showTimeSelect={true}
                            timeIntervals={15}
                            disabled={readOnlyFieldsContains('appointment.startDate')}
                          />
                        </InputBlock>
                        <ErrorMessage name="appointment.startDate">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                      </>
                    )}
                    {readOnlyFieldsContains('appointment.startDate') && (
                      <InputBlock title="Start Date & Time">
                        <Field
                          type="text"
                          name="appointment.startDate"
                          render={() => {
                            return (
                              <input
                                disabled={true}
                                name="appointment.startDate"
                                value={dayjs(values.appointment.startDate).format('MMMM D, YYYY h:mm A')}
                              />
                            );
                          }}
                        />
                      </InputBlock>
                    )}
                    {values.hasTranscriptionTask && (
                      <>
                        {!readOnlyFieldsContains('transcriptionDetails.replayGoodFrom') && (
                          <>
                            <InputBlock title="Replay Good From">
                              <DatePickerField
                                name="transcriptionDetails.replayGoodFrom"
                                value={values.transcriptionDetails.replayGoodFrom}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                disabled={true}
                              />
                            </InputBlock>
                            <ErrorMessage name="transcriptionDetails.replayGoodFrom">
                              {msg => <div className="error error-message">{msg}</div>}
                            </ErrorMessage>
                          </>
                        )}
                        {readOnlyFieldsContains('transcriptionDetails.replayGoodFrom') && (
                          <InputBlock title="Replay Good From">
                            <Field
                              type="text"
                              name="transcriptionDetails.replayGoodFrom"
                              render={() => {
                                return (
                                  <input
                                    disabled={true}
                                    name="transcriptionDetails.replayGoodFrom"
                                    value={dayjs(values.transcriptionDetails.replayGoodFrom).format(
                                      'MMMM D, YYYY h:mm A'
                                    )}
                                  />
                                );
                              }}
                            />
                          </InputBlock>
                        )}
                        {!hiddenFieldsContains('transcriptionDetails.replayType') && (
                          <>
                            <InputBlock title="Replay Type">
                              <Field
                                name="transcriptionDetails.replayType"
                                render={({ form }: { [key: string]: any }) => {
                                  return (
                                    <select
                                      required
                                      onBlur={() => setFieldTouched('transcriptionDetails.replayType')}
                                      onChange={e => {
                                        form.setFieldValue('transcriptionDetails.replayType', e.currentTarget.value);
                                        if (e.currentTarget.value == Bixby.Enums.ReplayType.Phone) {
                                          form.setFieldValue('transcriptionDetails.replayStreamingUrl', '');
                                        }
                                        if (e.currentTarget.value == Bixby.Enums.ReplayType.Streaming) {
                                          form.setFieldValue('transcriptionDetails.replayPhoneNumber', '');
                                        }
                                      }}>
                                      <option value={Bixby.Enums.ReplayType.Phone} key={Bixby.Enums.ReplayType.Phone}>
                                        {Bixby.Enums.ReplayType.Phone}
                                      </option>
                                      <option
                                        value={Bixby.Enums.ReplayType.Streaming}
                                        key={Bixby.Enums.ReplayType.Streaming}>
                                        {Bixby.Enums.ReplayType.Streaming}
                                      </option>
                                    </select>
                                  );
                                }}
                              />
                            </InputBlock>
                            <ErrorMessage name="newsType_Id">
                              {msg => <div className="error error-message">{msg}</div>}
                            </ErrorMessage>
                          </>
                        )}
                        {!hiddenFieldsContains('transcriptionDetails.assignees') && (
                          <PeopleSearchBox
                            title="Assign Transcription Task"
                            onChange={(ids: string[]) => setFieldValue('transcriptionDetails.assignees', ids)}
                            showMnpiUsers={props.adminCompany?.declarationType === DeclarationType.Private}
                          />
                        )}
                      </>
                    )}
                  </div>
                  <div className="profile-input-column">
                    {!readOnlyFieldsContains('appointment.endDate') && (
                      <>
                        <InputBlock title="End Date & Time">
                          <DatePickerField
                            disabled={readOnlyFieldsContains('appointment.endDate')}
                            name="appointment.endDate"
                            value={values.appointment.endDate}
                            onChange={(_field, value) => {
                              handleEndDateChange(setFieldValue, values, value);
                            }}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            showTimeSelect={true}
                            timeIntervals={15}
                          />
                        </InputBlock>
                        <ErrorMessage name="appointment.endDate">
                          {msg => <div className="error error-message">{msg}</div>}
                        </ErrorMessage>
                      </>
                    )}
                    {readOnlyFieldsContains('appointment.endDate') && (
                      <InputBlock title="End Date & Time">
                        <Field
                          type="text"
                          name="appointment.endDate"
                          render={() => {
                            return (
                              <input
                                disabled={true}
                                name="appointment.endDate"
                                value={dayjs(values.appointment.endDate).format('MMMM D, YYYY h:mm A')}
                              />
                            );
                          }}
                        />
                      </InputBlock>
                    )}
                    {values.hasTranscriptionTask && (
                      <>
                        <div
                          className={classnames('expiration_contain', {
                            hide_expiration_field: hiddenFieldsContains('transcriptionDetails.daysUntilExpiration'),
                          })}>
                          {!hiddenFieldsContains('transcriptionDetails.daysUntilExpiration') && (
                            <div>
                              <InputBlock title="Days Until Expiration">
                                <Field
                                  type="text"
                                  name="transcriptionDetails.daysUntilExpiration"
                                  render={({ field }: { [key: string]: any }) => {
                                    return (
                                      <input
                                        name="transcriptionDetails.daysUntilExpiration"
                                        placeholder="Days Until Expiration"
                                        onBlur={handleBlur}
                                        value={field.value}
                                        type="number"
                                        disabled={!values?.transcriptionDetails?.replayGoodFrom}
                                        min={1}
                                        onChange={e => {
                                          handleDaysUntilExpirationChange(
                                            setFieldValue,
                                            values,
                                            parseInt(e.target.value)
                                          );
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </InputBlock>
                              <ErrorMessage name="transcriptionDetails.replayPhoneNumber">
                                {msg => <div className="error error-message">{msg}</div>}
                              </ErrorMessage>
                            </div>
                          )}
                          <div>
                            {!readOnlyFieldsContains('transcriptionDetails.replayGoodUntil') && (
                              <>
                                <InputBlock title="Replay Good Until">
                                  <DatePickerField
                                    name="transcriptionDetails.replayGoodUntil"
                                    value={values.transcriptionDetails.replayGoodUntil}
                                    onChange={(_field, value) => {
                                      handleReplayGoodUntilChange(setFieldValue, values, value);
                                    }}
                                    dateFormat="MMMM d, yyyy"
                                    showTimeSelect={false}
                                    disabled={readOnlyFieldsContains('transcriptionDetails.replayGoodUntil')}
                                  />
                                </InputBlock>
                                <ErrorMessage name="transcriptionDetails.replayGoodUntil">
                                  {msg => <div className="error error-message">{msg}</div>}
                                </ErrorMessage>
                              </>
                            )}
                            {readOnlyFieldsContains('transcriptionDetails.replayGoodUntil') && (
                              <InputBlock title="Replay Good Until">
                                <Field
                                  type="text"
                                  name="transcriptionDetails.replayGoodUntil"
                                  render={() => {
                                    return (
                                      <input
                                        disabled={true}
                                        name="transcriptionDetails.replayGoodUntil"
                                        value={dayjs(values.transcriptionDetails.replayGoodUntil).format(
                                          'MMMM D, YYYY h:mm A'
                                        )}
                                      />
                                    );
                                  }}
                                />
                              </InputBlock>
                            )}
                          </div>
                        </div>
                        {values.transcriptionDetails.replayType == Bixby.Enums.ReplayType.Phone &&
                          !hiddenFieldsContains('transcriptionDetails.replayPhoneNumber') && (
                            <>
                              <InputBlock title="Replay Phone Number">
                                <Field
                                  type="text"
                                  name="transcriptionDetails.replayPhoneNumber"
                                  render={({ form, field }: { [key: string]: any }) => {
                                    return (
                                      <input
                                        name="transcriptionDetails.replayPhoneNumber"
                                        placeholder="Enter Replay Phone Number"
                                        onBlur={handleBlur}
                                        value={field.value}
                                        onChange={e => {
                                          form.setFieldValue('transcriptionDetails.replayPhoneNumber', e.target.value);
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </InputBlock>
                              <ErrorMessage name="transcriptionDetails.replayPhoneNumber">
                                {msg => <div className="error error-message">{msg}</div>}
                              </ErrorMessage>
                            </>
                          )}
                        {values.transcriptionDetails.replayType == Bixby.Enums.ReplayType.Streaming &&
                          !hiddenFieldsContains('transcriptionDetails.replayStreamingUrl') && (
                            <>
                              <InputBlock title="Replay Streaming URL">
                                <Field
                                  type="text"
                                  name="transcriptionDetails.replayStreamingUrl"
                                  render={({ form, field }: { [key: string]: any }) => {
                                    return (
                                      <input
                                        name="transcriptionDetails.replayStreamingUrl"
                                        placeholder="Enter Replay Streaming URL"
                                        onBlur={handleBlur}
                                        value={field.value}
                                        onChange={e => {
                                          form.setFieldValue('transcriptionDetails.replayStreamingUrl', e.target.value);
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </InputBlock>
                              <ErrorMessage name="transcriptionDetails.replayStreamingUrl">
                                {msg => <div className="error error-message">{msg}</div>}
                              </ErrorMessage>
                            </>
                          )}

                        {!hiddenFieldsContains('transcriptionDetails.replayStreamingUrl') && (
                          <>
                            <InputBlock title="Replay Pin">
                              <Field
                                type="text"
                                name="transcriptionDetails.replayPin"
                                render={({ form, field }: { [key: string]: any }) => {
                                  return (
                                    <input
                                      name="transcriptionDetails.replayPin"
                                      placeholder="Enter Replay Pin"
                                      onBlur={handleBlur}
                                      value={field.value}
                                      onChange={e => {
                                        form.setFieldValue('transcriptionDetails.replayPin', e.target.value);
                                      }}
                                    />
                                  );
                                }}
                              />
                            </InputBlock>
                            <ErrorMessage name="transcriptionDetails.replayReservationNumber">
                              {msg => <div className="error error-message">{msg}</div>}
                            </ErrorMessage>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </React.Fragment>
              )}
              {additionalDetails && (
                <div className="search_block">
                  <div dangerouslySetInnerHTML={{ __html: additionalDetails }}></div>
                </div>
              )}
              {isDocumentHostingEnabled && (
                <>
                  {adminCompany && !Bixby.Utils.isNullOrEmpty(adminCompany) && adminCompany.isWatermarkLocked && (
                    <p className="error watermark-error">
                      This is a locked watermarked site. You must use the Bulk Upload feature if you would like to
                      upload documents.
                    </p>
                  )}
                  {adminCompany && !Bixby.Utils.isNullOrEmpty(adminCompany) && !adminCompany.isWatermarkLocked && (
                    <>
                      <CompanyDocumentFileUpload
                        currentCompany={adminCompany}
                        companyDocumentCategories={companyDocumentCategories || []}
                        droppedFiles={values.droppedFiles}
                        onSetDroppedFiles={files => handleSetDroppedFiles(setFieldValue, files)}
                        dispatchToastError={dispatchToastError}
                        rejectWatermarks
                        onDownloadDocument={async () => void 0}
                      />
                      <div className="document-validation-contain">
                        <BixButton
                          type="button"
                          useSmall
                          disabled={
                            values.droppedFiles.length === 0 || isValidatingDocuments
                              ? true
                              : values.droppedFiles.some(
                                  file =>
                                    file.hasDuplicateNameValidationError !== false ||
                                    !file.companyDocumentCompanyDocumentCategories ||
                                    !file.title
                                )
                          }
                          useLoading
                          isLoading={isValidatingDocuments}
                          onClick={() => handleValidateDocuments(setFieldValue, values.droppedFiles)}>
                          Validate Documents
                        </BixButton>
                      </div>
                      {!!documentValidationError && <p className="error">{documentValidationError}</p>}
                    </>
                  )}
                </>
              )}
              <div className="email_template">
                <InputBlock title="Mail Body">
                  <Field
                    name="entry"
                    render={({ field, form }: { [key: string]: any }) => {
                      const handleMailBodyOnBlur = () => {
                        form.setFieldTouched('entry');
                      };
                      return (
                        <BixbyQuillWrapper
                          value={field.value}
                          onBlur={handleMailBodyOnBlur}
                          onChange={field.onChange(field.name)}
                        />
                      );
                    }}
                  />
                </InputBlock>
                <ErrorMessage name="entry">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                {company && (
                  <div className="acceptance_wrap">
                    <Field
                      name="relatedText"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox id="relatedText" checked={field.value} onChange={field.onChange(field.name)}>
                          I confer all text relates to {company.name}
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="relatedText">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                )}
                {/* {company && isDocumentHostingEnabled && values.droppedFiles.length === 0 && (
                  <div className="acceptance_wrap">
                    <Field
                      name="emptyDocuments"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="emptyDocuments"
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setNoDocumentsCheckValue(target.checked);
                          }}>
                          I confirm no documents to upload
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="emptyDocuments">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                )} */}
                {(extraConferSteps || []).map((extraConferStep, ix) => (
                  <React.Fragment key={ix}>
                    <Field
                      name={extraConferStep.name}
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id={field.name}
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setFieldValue(field.name, target.checked);
                          }}
                          postLabelRender={extraConferStep.postLabelRender}
                          disabled={readOnlyFieldsContains(extraConferStep.name)}>
                          {extraConferStep.label}
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name={extraConferStep.name}>
                      {() => <div className="error error-message">{extraConferStep.errorMessage}</div>}
                    </ErrorMessage>
                  </React.Fragment>
                ))}
                {currentlySubmittingDocumentStatus && (
                  <p>
                    {currentlySubmittingDocumentStatus} <Loading inline />
                  </p>
                )}
                <div className="button_wrap">
                  {!hiddenFieldsContains('previewButton') && (
                    <BixButton light type="button" onClick={handlePreviewModalClick}>
                      Preview
                    </BixButton>
                  )}
                  <BixButton
                    type="submit"
                    disabled={
                      isSubmittingNews || !!currentlySubmittingDocumentStatus
                      // || (isDocumentHostingEnabled && values.droppedFiles.length === 0 && !noDocumentsCheckValue)
                    }
                    isLoading={isSubmittingNews}>
                    Upload to Portal
                  </BixButton>
                </div>
              </div>
              <Modal
                title={
                  modalType === Bixby.Enums.ModalTypes.PreviewModal
                    ? 'Company Update Preview'
                    : 'Please confirm your company update.'
                }
                active={modalType !== Bixby.Enums.ModalTypes.None}
                onClose={handleCloseModal}>
                {modalType === Bixby.Enums.ModalTypes.PreviewModal && (
                  <div className="modal_news_item">
                    <NewsItem
                      key={values.company_Id}
                      id={null}
                      category={alertTypeById(values.newsType_Id)}
                      title={values.subject}
                      date={values.date}
                      aboutUrl={`/company/${values.company_Id}`}
                      aboutTitle={company ? company.name : ''}
                      description={values.description}
                      getNewsEntry={() => {
                        return Promise.resolve({
                          entry: values.entry,
                        } as Bixby.Models.News);
                      }}
                    />
                  </div>
                )}
                {modalType === Bixby.Enums.ModalTypes.ConfirmModal && (
                  <div className="modal_button_container">
                    <BixButton light type="button" onClick={handleCloseModal}>
                      Cancel
                    </BixButton>
                    <BixButton type="submit" onClick={handleSubmitClick}>
                      Confirm
                    </BixButton>
                  </div>
                )}
              </Modal>
            </form>
          </div>
        )}></Formik>
    </section>
  );
};

export default CreateNewsForm;
